/**
 * SearchBar Component
 * 
 * A reusable search input with debounce, clear button, and keyboard shortcuts.
 * Supports optional JQL-style advanced mode toggle.
 */

import { useState, useEffect, useRef, useCallback, type KeyboardEvent } from 'react';
import { Search, X, Command, Sliders } from 'lucide-react';
import type { SearchBarProps } from './types';

// ============================================================================
// Hook for debounced value
// ============================================================================

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);
    
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  
  return debouncedValue;
}

// ============================================================================
// Component
// ============================================================================

export function SearchBar({
  value,
  onChange,
  placeholder = 'Search...',
  debounceMs = 300,
  showAdvanced = false,
  onAdvancedClick,
  className = '',
}: SearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(value);
  const debouncedValue = useDebouncedValue(localValue, debounceMs);
  
  // Sync local value with prop
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  // Call onChange when debounced value changes
  useEffect(() => {
    if (debouncedValue !== value) {
      onChange(debouncedValue);
    }
  }, [debouncedValue, onChange, value]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Cmd+K or Ctrl+K to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      
      // Escape to clear and blur
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        if (localValue) {
          setLocalValue('');
          onChange('');
        } else {
          inputRef.current?.blur();
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [localValue, onChange]);
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);
  
  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);
  
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    // Enter to trigger immediate search
    if (e.key === 'Enter') {
      onChange(localValue);
    }
  }, [localValue, onChange]);
  
  return (
    <div className={`relative flex items-center ${className}`}>
      {/* Search icon */}
      <div className="absolute left-3 flex items-center pointer-events-none">
        <Search className="h-4 w-4 text-[var(--text-ter)]" />
      </div>
      
      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full pl-10 pr-24 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder:text-[var(--text-ter)] bg-[var(--bg-raised)]"
      />
      
      {/* Right side actions */}
      <div className="absolute right-2 flex items-center gap-1">
        {/* Keyboard shortcut hint */}
        {!localValue && (
          <div className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-[var(--text-ter)] bg-[var(--hover-tint)] rounded border border-[var(--rule)]">
            <Command className="h-3 w-3" />
            <span>K</span>
          </div>
        )}
        
        {/* Clear button */}
        {localValue && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] focus:outline-none focus:ring-2 focus:ring-primary-500"
            title="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        
        {/* Advanced mode toggle */}
        {showAdvanced && onAdvancedClick && (
          <button
            type="button"
            onClick={onAdvancedClick}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)] focus:outline-none focus:ring-2 focus:ring-primary-500"
            title="Advanced search"
          >
            <Sliders className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default SearchBar;
